import React, { useMemo } from 'react'
import {
  filter,
  find,
  groupBy,
  isNil,
  keys,
  map,
  mapValues,
  reduce,
  size,
  some,
  split,
  toLower,
  trim,
} from 'lodash'
import PropTypes from 'prop-types'

import { AI_HIGHLIGHT_STYLES } from '../../constants'
import { findWordIndexInDraftJsText } from '../../helpers'
import { StyledAiHighlightedText } from '../styled'

const AiHighlightDecoratedComponent = ({
  children,
  highlights,
  textBlocksWithIndexes,
}) => {
  const { text, start } = children[0].props

  const highlightsGroupedByMatchType = useMemo(
    () =>
      mapValues(groupBy(highlights, 'matchType'), wordList =>
        map(wordList, ({ word, override, startIndex }) => ({
          word: toLower(trim(word)),
          override,
          startIndex,
        })),
      ),
    [highlights],
  )

  const alternativeTypeHighlights = useMemo(
    () =>
      map(
        filter(highlights, ({ alternativeType }) => alternativeType),
        highlightingWord => ({
          ...highlightingWord,
          alternativeType: AI_HIGHLIGHT_STYLES.alternativeType.value,
        }),
      ),
    [highlights],
  )

  const highlightsGroupedByAlternativeType = useMemo(
    () => ({
      [AI_HIGHLIGHT_STYLES.alternativeType.value]: map(
        alternativeTypeHighlights,
        ({ word, override, startIndex }) => ({
          word: toLower(trim(word)),
          override,
          startIndex,
        }),
      ),
    }),
    [alternativeTypeHighlights],
  )

  const highlightsToSearch = {
    ...highlightsGroupedByAlternativeType,
    ...highlightsGroupedByMatchType,
  }
  const availableHighlightStyles = [
    ...keys(highlightsGroupedByAlternativeType),
    ...keys(highlightsGroupedByMatchType),
  ]

  const matchingHighlightStyle = find(
    availableHighlightStyles,
    highlightStyle =>
      some(highlightsToSearch[highlightStyle], ({ word, startIndex }) => {
        const isTextComposedOfASingleBlock = size(textBlocksWithIndexes) === 1

        if (!isNil(startIndex) && isTextComposedOfASingleBlock) {
          return (
            findWordIndexInDraftJsText(text, word) !== -1 &&
            startIndex === start
          )
        }

        return findWordIndexInDraftJsText(text, word) !== -1
      }),
  )

  const isOverride = some(
    highlightsToSearch[matchingHighlightStyle],
    ({ word, override }) =>
      findWordIndexInDraftJsText(text, word) !== -1 && override,
  )

  const matchingHighlightStyleParts = split(matchingHighlightStyle, ':')
  let highlightsToApply = reduce(
    matchingHighlightStyleParts,
    (result, part) => {
      if (!isNil(AI_HIGHLIGHT_STYLES[part])) {
        return [...result, AI_HIGHLIGHT_STYLES[part].value]
      }

      return result
    },
    [],
  )

  if (isOverride) {
    highlightsToApply = [
      ...highlightsToApply,
      AI_HIGHLIGHT_STYLES.overrides.value,
    ]
  }

  return (
    <StyledAiHighlightedText highlights={highlightsToApply}>
      {children}
    </StyledAiHighlightedText>
  )
}

AiHighlightDecoratedComponent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  highlights: PropTypes.arrayOf(
    PropTypes.shape({
      word: PropTypes.string.isRequired,
      matchType: PropTypes.string,
      override: PropTypes.bool,
    }),
  ).isRequired,
  textBlocksWithIndexes: PropTypes.arrayOf(
    PropTypes.shape({
      startIndex: PropTypes.number,
      endIndex: PropTypes.number,
      text: PropTypes.string,
    }),
  ).isRequired,
}

export default AiHighlightDecoratedComponent
